import type { Column, SortDirection } from '@tanstack/react-table';
import { Check, Search } from 'lucide-react';
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useAccountingCategories } from '@/api/accounting-categories/accounting-categories.queries';
import { type BadgeColor, BadgeOptionLabel } from '@/components/Badge';
import { Checkbox } from '@/components/Checkbox';
import { DatePicker } from '@/components/DatePicker';
import { Input } from '@/components/Input';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { isMatchingFilter } from '@/utils/string.utils';
import { cn } from '@/utils/style';
import { useTablePagination } from './TablePaginationContext';
import {
  CHECKBOX_FILTER_TYPES,
  type DateRangeFilterValue,
  type FilterOption,
  type RangeFilterValue,
} from './table.types';
import { useUniqueColumnValues } from './useUniqueColumnValues';

const SEARCH_FILTER_DEBOUNCE_MS = 300;
const LOADING_ALL_ROWS_LABEL = 'Chargement de toutes les lignes…';

interface ColumnFilterDropdownProps<TData> {
  column: Column<TData, unknown>;
  data: TData[];
}

interface SortSectionProps {
  currentSortDirection: false | SortDirection;
  onToggleSorting: (desc: boolean) => void;
  isNumeric?: boolean;
  hasBorder?: boolean;
}

function SortSection({
  currentSortDirection,
  onToggleSorting,
  isNumeric,
  hasBorder,
}: SortSectionProps) {
  return (
    <div className={cn('flex flex-col p-1', hasBorder && 'border-b border-gray-200')}>
      <button
        type="button"
        className={cn(
          'flex items-center justify-between px-2 py-1.5 text-sm hover:bg-gray-100 text-left rounded',
          currentSortDirection === 'asc' && 'bg-gray-100'
        )}
        onClick={() => onToggleSorting(false)}
      >
        <span>{isNumeric ? 'Par ordre croissant' : 'Trier de A à Z'}</span>
        {currentSortDirection === 'asc' && <Check className="size-4" />}
      </button>
      <button
        type="button"
        className={cn(
          'flex items-center justify-between px-2 py-1.5 text-sm hover:bg-gray-100 text-left rounded',
          currentSortDirection === 'desc' && 'bg-gray-100'
        )}
        onClick={() => onToggleSorting(true)}
      >
        <span>{isNumeric ? 'Par ordre décroissant' : 'Trier de Z à A'}</span>
        {currentSortDirection === 'desc' && <Check className="size-4" />}
      </button>
    </div>
  );
}

interface DisplayOption {
  value: unknown;
  label: string;
  color?: BadgeColor;
  group?: string;
}

interface CheckboxListProps {
  options: DisplayOption[];
  selectedValues: unknown[];
  onToggleValue: (value: unknown) => void;
  isBadgeEnum?: boolean;
}

function CheckboxList({ options, selectedValues, onToggleValue, isBadgeEnum }: CheckboxListProps) {
  const listId = useId();
  const isGrouped = options.some((option) => option.group);
  const sortedOptions = useMemo(
    () =>
      isGrouped
        ? options
        : [...options].sort((a, b) => {
            const aSelected = selectedValues.includes(a.value) ? 0 : 1;
            const bSelected = selectedValues.includes(b.value) ? 0 : 1;
            return aSelected - bSelected;
          }),
    [isGrouped, options, selectedValues]
  );

  if (sortedOptions.length === 0) {
    return <div className="px-3 py-4 text-sm text-gray-500 text-center">Aucun résultat</div>;
  }

  return (
    <>
      {sortedOptions.map((option, index) => {
        const isSelected = selectedValues.includes(option.value);

        if (isBadgeEnum && option.color) {
          const startsGroup = option.group && option.group !== sortedOptions[index - 1]?.group;
          return (
            <Fragment key={String(option.value)}>
              {startsGroup && (
                <div className="px-3 pt-2 pb-1 text-xs font-medium text-gray-500">
                  {option.group}
                </div>
              )}
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer w-full text-left"
                onClick={() => onToggleValue(option.value)}
              >
                <BadgeOptionLabel color={option.color} label={option.label} />
                {isSelected && <Check className="size-4 text-gray-900" />}
              </button>
            </Fragment>
          );
        }

        // A label, not a button: the checkbox is itself a button, and buttons do not nest.
        return (
          <label
            key={String(option.value)}
            htmlFor={`${listId}-${index}`}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer w-full text-left"
          >
            <Checkbox
              id={`${listId}-${index}`}
              checked={isSelected}
              onCheckedChange={() => onToggleValue(option.value)}
            />
            <span className="text-sm truncate">{option.label}</span>
          </label>
        );
      })}
    </>
  );
}

function OptionsLoading() {
  return (
    <output className="block px-3 py-4 text-sm text-gray-500 text-center">
      {LOADING_ALL_ROWS_LABEL}
    </output>
  );
}

interface TextFilterProps {
  options: DisplayOption[];
  selectedValues: unknown[];
  onToggleValue: (value: unknown) => void;
  isLoadingOptions: boolean;
}

function TextFilter({ options, selectedValues, onToggleValue, isLoadingOptions }: TextFilterProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    return options.filter((opt) => isMatchingFilter(opt.label, searchQuery));
  }, [options, searchQuery]);

  return (
    <>
      <div className="p-2 border-b border-gray-200">
        <Input
          icon={<Search />}
          placeholder="Rechercher..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      <div className="max-h-48 overflow-y-auto">
        {isLoadingOptions ? (
          <OptionsLoading />
        ) : (
          <CheckboxList
            options={filteredOptions}
            selectedValues={selectedValues}
            onToggleValue={onToggleValue}
          />
        )}
      </div>
    </>
  );
}

interface EnumFilterProps {
  options: DisplayOption[];
  selectedValues: unknown[];
  onToggleValue: (value: unknown) => void;
  isBadgeEnum?: boolean;
  isLoadingOptions: boolean;
}

function EnumFilter({
  options,
  selectedValues,
  onToggleValue,
  isBadgeEnum,
  isLoadingOptions,
}: EnumFilterProps) {
  const isGrouped = options.some((option) => option.group);
  return (
    <div className={cn('overflow-y-auto', isGrouped ? 'max-h-96' : 'max-h-48')}>
      {isLoadingOptions ? (
        <OptionsLoading />
      ) : (
        <CheckboxList
          options={options}
          selectedValues={selectedValues}
          onToggleValue={onToggleValue}
          isBadgeEnum={isBadgeEnum}
        />
      )}
    </div>
  );
}

interface SearchFilterProps {
  value: string;
  onChange: (value: string | undefined) => void;
}

// The value reaches the column (and, on a paginated table, the server) one pause after typing.
function SearchFilter({ value, onChange }: SearchFilterProps) {
  const [draft, setDraft] = useState(value);
  const debouncedDraft = useDebouncedValue(draft, SEARCH_FILTER_DEBOUNCE_MS);
  const emittedRef = useRef(value);
  useEffect(() => {
    if (debouncedDraft === emittedRef.current) return;
    emittedRef.current = debouncedDraft;
    onChange(debouncedDraft || undefined);
  }, [debouncedDraft, onChange]);
  // Cleared from outside (« Réinitialiser les filtres ») while open: follow, don't re-apply.
  useEffect(() => {
    if (value !== emittedRef.current) {
      emittedRef.current = value;
      setDraft(value);
    }
  }, [value]);

  return (
    <div className="p-2 border-t border-gray-200">
      <Input
        icon={<Search />}
        placeholder="Contient..."
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="h-8 text-sm"
      />
    </div>
  );
}

// On a paginated table the rows are a slice: the options come from a declared complete list, or
// (`load-all`) from the rows once every page is in.
function usePaginatedFilterOptions<TData>(
  column: Column<TData, unknown>,
  data: TData[],
  isCheckboxFilter: boolean
): { options: DisplayOption[]; isLoading: boolean } {
  const meta = column.columnDef.meta;
  const pagination = useTablePagination();
  const declaredOptions = meta?.useFilterOptions?.() ?? meta?.filterOptions;
  const uniqueValues = useUniqueColumnValues(column, data);

  const needsAllRows = isCheckboxFilter && !declaredOptions && !meta?.options;
  const requestAllRows = pagination?.requestAllRows;
  useEffect(() => {
    if (needsAllRows && pagination?.filtering === 'load-all') requestAllRows?.();
  }, [needsAllRows, pagination?.filtering, requestAllRows]);

  return useMemo(() => {
    if (declaredOptions) return { options: declaredOptions, isLoading: false };
    if (meta?.filterType === 'badge-enum' && meta.options) {
      return { options: meta.options, isLoading: false };
    }
    if (meta?.useFilterOptions) return { options: [], isLoading: true };
    const isLoading = !!pagination && !pagination.isComplete;
    return { options: isLoading ? [] : uniqueValues.map(toDisplayOption), isLoading };
  }, [declaredOptions, meta, pagination, uniqueValues]);
}

function toDisplayOption(option: FilterOption<unknown> | { value: unknown; label: string }) {
  return { value: option.value, label: option.label };
}

// A range with neither bound is no filter at all.
function withBound<T>(
  range: { min?: T | null; max?: T | null },
  bound: 'min' | 'max',
  boundValue: T | null
) {
  const next = { ...range, [bound]: boundValue };
  return next.min == null && next.max == null ? undefined : next;
}

interface NumericRangeFilterProps {
  value: RangeFilterValue;
  onChange: (value: RangeFilterValue | undefined) => void;
}

function NumericRangeFilter({ value, onChange }: NumericRangeFilterProps) {
  const handleChange = (bound: 'min' | 'max') => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange(
      withBound(value, bound, e.target.value === '' ? null : Number.parseFloat(e.target.value))
    );

  return (
    <div className="p-2 flex flex-col gap-2 border-t border-gray-200">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600 w-10">Min</span>
        <Input
          type="number"
          placeholder="Min"
          value={value.min ?? ''}
          onChange={handleChange('min')}
          className="h-8 text-sm flex-1"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600 w-10">Max</span>
        <Input
          type="number"
          placeholder="Max"
          value={value.max ?? ''}
          onChange={handleChange('max')}
          className="h-8 text-sm flex-1"
        />
      </div>
    </div>
  );
}

interface DateRangeFilterProps {
  value: DateRangeFilterValue;
  onChange: (value: DateRangeFilterValue | undefined) => void;
}

function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  return (
    <div className="p-2 flex flex-col gap-2 border-t border-gray-200">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600 w-10">Min</span>
        <DatePicker
          value={value.min}
          onChange={(date) => onChange(withBound(value, 'min', date))}
          className="flex-1"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600 w-10">Max</span>
        <DatePicker
          value={value.max}
          onChange={(date) => onChange(withBound(value, 'max', date))}
          className="flex-1"
        />
      </div>
    </div>
  );
}

function isDateLikeValue(value: unknown): boolean {
  if (value instanceof Date) return true;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return !Number.isNaN(parsed.getTime()) && value.includes('-');
  }
  return false;
}

function detectNumericType<TData>(
  column: Column<TData, unknown>,
  data: TData[]
): 'date' | 'number' {
  const accessorFn = column.accessorFn;

  for (const row of data) {
    const value = accessorFn ? accessorFn(row, 0) : (row as Record<string, unknown>)[column.id];
    if (value == null) continue;
    if (isDateLikeValue(value)) return 'date';
    if (typeof value === 'number') return 'number';
  }
  return 'number';
}

interface AccountingCategoryFilterProps {
  selectedValues: unknown[];
  onToggleValue: (value: unknown) => void;
}

function AccountingCategoryFilter({
  selectedValues,
  onToggleValue,
}: AccountingCategoryFilterProps) {
  const { data: categories = [] } = useAccountingCategories();
  const [searchQuery, setSearchQuery] = useState('');
  const listId = useId();

  const topLevelCategories = useMemo(
    () => categories.filter((c) => c.parentId === null),
    [categories]
  );

  const filteredTree = useMemo(() => {
    if (!searchQuery) return topLevelCategories;
    return topLevelCategories
      .map((parent) => {
        const matchingChildren = parent.children.filter((child) =>
          isMatchingFilter(child.name, searchQuery)
        );
        if (isMatchingFilter(parent.name, searchQuery)) return parent;
        if (matchingChildren.length > 0) return { ...parent, children: matchingChildren };
        return null;
      })
      .filter(Boolean) as typeof topLevelCategories;
  }, [searchQuery, topLevelCategories]);

  return (
    <>
      <div className="p-2 border-b border-gray-200">
        <Input
          icon={<Search />}
          placeholder="Rechercher..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      <div className="max-h-48 overflow-y-auto">
        {filteredTree.length === 0 ? (
          <div className="px-3 py-4 text-sm text-gray-500 text-center">Aucun résultat</div>
        ) : (
          filteredTree.map((parent) => (
            <div key={parent.id}>
              <label
                htmlFor={`${listId}-${parent.id}`}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer w-full text-left"
              >
                <Checkbox
                  id={`${listId}-${parent.id}`}
                  checked={selectedValues.includes(parent.id)}
                  onCheckedChange={() => onToggleValue(parent.id)}
                />
                <span className="text-sm truncate font-medium">{parent.name}</span>
              </label>
              {parent.children.map((child) => (
                <label
                  key={child.id}
                  htmlFor={`${listId}-${child.id}`}
                  className="flex items-center gap-2 px-3 py-2 pl-6 hover:bg-gray-50 cursor-pointer w-full text-left"
                >
                  <Checkbox
                    id={`${listId}-${child.id}`}
                    checked={selectedValues.includes(child.id)}
                    onCheckedChange={() => onToggleValue(child.id)}
                  />
                  <span className="text-sm truncate">{child.name}</span>
                </label>
              ))}
            </div>
          ))
        )}
      </div>
    </>
  );
}

export function ColumnFilterDropdown<TData>({ column, data }: ColumnFilterDropdownProps<TData>) {
  const meta = column.columnDef.meta;
  const filterType = meta?.filterType ?? 'none';

  const isRangeFilter = filterType === 'numeric' || filterType === 'date';
  const hasCheckboxList =
    CHECKBOX_FILTER_TYPES.includes(filterType) || filterType === 'accounting-category';
  const { options: displayOptions, isLoading: isLoadingOptions } = usePaginatedFilterOptions(
    column,
    data,
    CHECKBOX_FILTER_TYPES.includes(filterType)
  );
  const currentFilterValue = column.getFilterValue();
  const currentSortDirection = column.getIsSorted();

  const numericType = useMemo(() => {
    if (filterType === 'date') return 'date';
    if (filterType !== 'numeric') return null;
    return detectNumericType(column, data);
  }, [filterType, data, column]);

  const arrayFilterValue = useMemo(() => {
    if (Array.isArray(currentFilterValue)) return currentFilterValue;
    return [];
  }, [currentFilterValue]);

  const rangeFilterValue = useMemo((): RangeFilterValue => {
    if (
      currentFilterValue &&
      typeof currentFilterValue === 'object' &&
      !Array.isArray(currentFilterValue)
    ) {
      return currentFilterValue as RangeFilterValue;
    }
    return {};
  }, [currentFilterValue]);

  const dateRangeFilterValue = useMemo((): DateRangeFilterValue => {
    if (
      currentFilterValue &&
      typeof currentFilterValue === 'object' &&
      !Array.isArray(currentFilterValue)
    ) {
      return currentFilterValue as DateRangeFilterValue;
    }
    return {};
  }, [currentFilterValue]);

  const handleToggleValue = useCallback(
    (value: unknown) => {
      const newValues = arrayFilterValue.includes(value)
        ? arrayFilterValue.filter((v) => v !== value)
        : [...arrayFilterValue, value];

      column.setFilterValue(newValues.length > 0 ? newValues : undefined);
    },
    [column, arrayFilterValue]
  );

  const handleToggleSorting = useCallback(
    (desc: boolean) => {
      if ((desc && currentSortDirection === 'desc') || (!desc && currentSortDirection === 'asc')) {
        column.clearSorting();
      } else {
        column.toggleSorting(desc);
      }
    },
    [column, currentSortDirection]
  );

  const handleNumericRangeChange = useCallback(
    (value: RangeFilterValue | undefined) => {
      column.setFilterValue(value);
    },
    [column]
  );

  const handleDateRangeChange = useCallback(
    (value: DateRangeFilterValue | undefined) => {
      column.setFilterValue(value);
    },
    [column]
  );

  const handleSearchChange = useCallback(
    (value: string | undefined) => {
      column.setFilterValue(value);
    },
    [column]
  );

  return (
    <div className="flex flex-col">
      <SortSection
        currentSortDirection={currentSortDirection}
        onToggleSorting={handleToggleSorting}
        isNumeric={isRangeFilter}
        hasBorder={hasCheckboxList || isRangeFilter || filterType === 'search'}
      />

      {filterType === 'text' && (
        <TextFilter
          options={displayOptions}
          selectedValues={arrayFilterValue}
          onToggleValue={handleToggleValue}
          isLoadingOptions={isLoadingOptions}
        />
      )}

      {(filterType === 'enum' || filterType === 'badge-enum') && (
        <EnumFilter
          options={displayOptions}
          selectedValues={arrayFilterValue}
          onToggleValue={handleToggleValue}
          isBadgeEnum={filterType === 'badge-enum'}
          isLoadingOptions={isLoadingOptions}
        />
      )}

      {filterType === 'search' && (
        <SearchFilter
          value={typeof currentFilterValue === 'string' ? currentFilterValue : ''}
          onChange={handleSearchChange}
        />
      )}

      {filterType === 'accounting-category' && (
        <AccountingCategoryFilter
          selectedValues={arrayFilterValue}
          onToggleValue={handleToggleValue}
        />
      )}

      {isRangeFilter && numericType === 'number' && (
        <NumericRangeFilter value={rangeFilterValue} onChange={handleNumericRangeChange} />
      )}

      {isRangeFilter && numericType === 'date' && (
        <DateRangeFilter value={dateRangeFilterValue} onChange={handleDateRangeChange} />
      )}
    </div>
  );
}
